/**
 * Capture request — what the client posts to `POST /api/paypal-orders/capture`
 * after the buyer approves a PayPal order. The server captures funds against
 * PayPal, then attempts to persist the local `orders` row from `checkoutData`.
 *
 * Why `checkoutData` is `unknown`-ish (a passthrough object) here and not a
 * full re-declaration of the heavy `CheckoutPayload` shape:
 *
 *  - The route handler today treats `checkoutData` as opaque metadata — it
 *    spreads it into `partial_payment_alerts.metadata` for reconciliation
 *    replay (`recordOrphanedCapture`) and into `buildOrderPayloadFromCapture`,
 *    which has its own internal expectations. There is no boundary parse on
 *    `checkoutData` in the capture route today.
 *  - The Stripe checkout-session route DOES schema-validate the same payload
 *    (see `stripe-checkout-session.ts` → `CheckoutPayloadSchema`). Capture
 *    deliberately stays looser to avoid coupling the PayPal capture path to a
 *    shape it does not author. Step-1 scope is "lock the request envelope so
 *    builders own assembly", not "introduce a new server-side rejection
 *    surface mid-money-flow".
 *  - Mobile's existing `capturePayPalOrder` (`apps/mobile/lib/api-paypal.ts`)
 *    already types `checkoutData: unknown`. Mirror that — the contract is the
 *    union of what both clients post, not what the server might enforce later.
 *
 * @see app/api/paypal-orders/capture/route.ts — handler
 * @see apps/mobile/lib/api-paypal.ts capturePayPalOrder — mobile sibling
 * @see lib/checkout-payload.ts — CheckoutPayload (what `checkoutData` morally is)
 */

import { z } from "zod";
import { pinned } from "./pin";

/**
 * Full request body for `POST /api/paypal-orders/capture`.
 *
 * Fields:
 *  - `order_id` — the PayPal Order ID returned from `/api/paypal-orders/create`.
 *    Required. Snake-case is load-bearing: the server destructures
 *    `request.json()` by name and the mobile + web clients both send it as
 *    `order_id`. Renaming is a breaking change.
 *  - `checkoutData` — optional opaque metadata. When present the server uses
 *    it to (a) drive `buildOrderPayloadFromCapture` for server-side order
 *    persistence and (b) seed `partial_payment_alerts.metadata` so the
 *    reconciliation cron can replay `createOrder` if persistence fails after
 *    capture (`.tondocs/orders/RECONCILIATION_JOB.md §0`). Shape is documented
 *    in `lib/checkout-payload.ts::CheckoutPayload` but intentionally not
 *    schema-pinned here — see header rationale.
 *  - `mockCode` — sandbox-only PayPal-Mock-Response trigger. Honoured only
 *    when both `PAYPAL_NEGATIVE_TESTING` and `IS_PAYPAL_SANDBOX` are true and
 *    the value is in the route's allowlist. Ignored otherwise.
 */
export interface CaptureOrderRequest {
  /** PayPal Order ID from the create step. Snake-case is on the wire. */
  order_id: string;
  /**
   * Optional checkout metadata used for server-side order persistence and
   * reconciliation recovery. Opaque at the contract layer; concrete shape
   * lives in `lib/checkout-payload.ts::CheckoutPayload`.
   */
  checkoutData?: Record<string, unknown>;
  /** Sandbox-only negative-testing code. Honoured under sandbox + demo only. */
  mockCode?: string;
}

// ---------------------------------------------------------------------------
// Runtime parse — Zod schema (boundary)
//
// `.passthrough()` on `checkoutData` so unknown keys round-trip — the server
// reads several fields from it (items, shippingInfo, etc.) and the surface is
// allowed to grow without contract churn. `z.object` itself is loose by
// default; we make the looseness explicit on the nested object.
// ---------------------------------------------------------------------------

export const CaptureOrderRequestSchema = pinned<CaptureOrderRequest>()(
  z.object({
    order_id: z.string().min(1),
    checkoutData: z.record(z.string(), z.unknown()).optional(),
    mockCode: z.string().optional(),
  }),
);

// ---------------------------------------------------------------------------
// Response contract — three success branches on the SAME 2xx body
// ---------------------------------------------------------------------------
//
// The route returns one of THREE shapes on success (all 200). Today the wire
// doesn't carry an explicit discriminator field; consumers branch on the
// presence of `existingOrder` / `paymentStatus` / `code`. A future server-side
// `kind: "fresh" | "duplicate" | "pending"` would let us use
// `z.discriminatedUnion` directly — out of scope for this PR (it'd be a
// breaking shape change for every client that already destructures the loose
// shape). Until then we ship:
//
//   - a flat `CaptureOrderResponseSchema` covering the union of all fields
//     (loose: extra fields pass), used to validate the wire,
//   - a `CaptureOrderResponseNarrowed` discriminated type clients can switch
//     on after running the parser through `narrowCaptureResponse()`.
//
// Branches:
//
//   1. **Fresh OK**: PayPal capture succeeded, order persisted.
//      Carries `capture` (raw PayPal payload), `debugId`, `summary`,
//      `orderResult` (may be undefined when checkoutData was absent),
//      no `existingOrder`, no `paymentStatus`.
//
//   2. **Duplicate (existing order)**: capture short-circuited because an
//      order already exists for `paypal_order_id` (auction-duplicate or
//      general double-click race). Carries `existingOrder: true` and
//      `code: "DUPLICATE_ORDER"`. `summary` is the minimal completed stub.
//
//   3. **Captured-but-pending**: capture succeeded, order persistence failed
//      (DB write threw or returned `success: false`). Money has moved;
//      reconciliation will retry the order creation. Carries
//      `paymentStatus: "captured_order_pending"` and
//      `code: "ORDER_CREATION_FAILED"`.
//
// @see app/api/paypal-orders/capture/route.ts — all three return sites
// @see apps/mobile/lib/api-paypal.ts capturePayPalOrder — current consumer (loose parse)
// @see lib/payments/paypal-capture-summary.ts — `CaptureSummary` source of truth
// @see .tondocs/payments/API_RESPONSE_AUDIT.md §4 + Open Q5

/**
 * Per-unit capture roll-up. Mirrors `UnitCaptureSummary` in
 * `lib/payments/paypal-capture-summary.ts` — the two declarations are
 * pinned to each other (see `_AssertUnitCaptureSummaryMirror` below).
 */
export interface CaptureUnit {
  referenceId: string;
  status: "completed" | "pending" | "failed";
  capturedAmount: number;
  /**
   * True when `capturedAmount` above is NOT trustworthy — a COMPLETED
   * capture on this unit had no usable `amount.value` from the provider
   * (F3, HANDOFF_CAPTURED_FACTS_2026_07_10.md). Mirrors server-side
   * `UnitCaptureSummary.capturedAmountUnknown`; included here so a future
   * client surface that reads it doesn't need a re-pin. `capturedAmount`
   * still reports its (possibly-understated) numeric sum for backward
   * compatibility — consumers that care about correctness MUST check this
   * flag first.
   */
  capturedAmountUnknown?: boolean;
  failureReason?: string;
  captureId?: string;
  actualPlatformFee?: number;
  payeeMerchantId?: string;
}

/**
 * Order-level capture roll-up. Mirrors `CaptureSummary` in
 * `lib/payments/paypal-capture-summary.ts`. Note: server-side `CaptureSummary`
 * carries an `amountUnknown` flag that surfaces only in operator-alert
 * branches; we include it here too so any future client surface that
 * exposes it doesn't need a re-pin.
 */
export interface CaptureSummary {
  totalAmount: number;
  capturedAmount: number;
  missingAmount: number;
  affectedSellers: string[];
  isPartial: boolean;
  amountUnknown: boolean;
  units: CaptureUnit[];
  /**
   * Final capture status — `"COMPLETED"`, `"PENDING"`, etc. PayPal's
   * `capture.status`. The route copies it onto `summary.status` so
   * consumers don't need to reach into `capture`.
   */
  status?: string;
}

/**
 * Server-side per-seller order-persistence outcome. `orderResult` is `null`
 * (omitted on the wire) when there was no `checkoutData` to persist from
 * (e.g. mobile native-wallet caller skipping server-side order creation).
 */
export interface CaptureOrderResult {
  success: boolean;
  orderId?: string;
  orderNumber?: string;
  error?: string;
  auctionSettled?: boolean;
  offerSettled?: boolean;
}

/**
 * Flat union of every field any of the three success branches may carry.
 * Use `narrowCaptureResponse()` to switch on branches type-safely.
 */
export interface CaptureOrderResponse {
  /**
   * Raw PayPal `/v2/checkout/orders/{id}/capture` payload. Opaque at the
   * contract level — consumers only depend on it being present (mobile
   * fallback uses `if (body.capture)` to treat a 4xx with capture as 2xx).
   * On the duplicate branch this is the minimal stub `{ status: "COMPLETED" }`.
   */
  capture: unknown;
  /** PayPal-Debug-Id for support tracing. Absent on the duplicate branch. */
  debugId?: string;
  summary: CaptureSummary;
  orderResult?: CaptureOrderResult;
  /** True on the duplicate branch only. */
  existingOrder?: true;
  /**
   * Machine code carried on the non-fresh branches:
   *   - `"DUPLICATE_ORDER"` on the duplicate branch
   *   - `"ORDER_CREATION_FAILED"` on the captured-pending branch
   * Absent on the fresh-OK branch.
   */
  code?: "DUPLICATE_ORDER" | "ORDER_CREATION_FAILED";
  /** Carried only on the captured-pending branch. */
  paymentStatus?: "captured_order_pending";
}

export const CaptureUnitSchema = pinned<CaptureUnit>()(
  z.object({
    referenceId: z.string(),
    status: z.enum(["completed", "pending", "failed"]),
    capturedAmount: z.number(),
    capturedAmountUnknown: z.boolean().optional(),
    failureReason: z.string().optional(),
    captureId: z.string().optional(),
    actualPlatformFee: z.number().optional(),
    payeeMerchantId: z.string().optional(),
  }),
);

export const CaptureSummarySchema = pinned<CaptureSummary>()(
  z.object({
    totalAmount: z.number(),
    capturedAmount: z.number(),
    missingAmount: z.number(),
    affectedSellers: z.array(z.string()),
    isPartial: z.boolean(),
    amountUnknown: z.boolean(),
    units: z.array(CaptureUnitSchema),
    status: z.string().optional(),
  }),
);

export const CaptureOrderResultSchema = pinned<CaptureOrderResult>()(
  z.object({
    success: z.boolean(),
    orderId: z.string().optional(),
    orderNumber: z.string().optional(),
    error: z.string().optional(),
    auctionSettled: z.boolean().optional(),
    offerSettled: z.boolean().optional(),
  }),
);

export const CaptureOrderResponseSchema = pinned<CaptureOrderResponse>()(
  z.object({
    capture: z.unknown(),
    debugId: z.string().optional(),
    summary: CaptureSummarySchema,
    orderResult: CaptureOrderResultSchema.optional(),
    existingOrder: z.literal(true).optional(),
    code: z.enum(["DUPLICATE_ORDER", "ORDER_CREATION_FAILED"]).optional(),
    paymentStatus: z.literal("captured_order_pending").optional(),
  }),
);

/**
 * Discriminated narrowing of a parsed `CaptureOrderResponse`. Clients call
 * this after `CaptureOrderResponseSchema.safeParse()` and `switch` on
 * `result.kind` with `default: unreachable(x: never)` — the compiler then
 * enforces exhaustive handling of all three branches.
 *
 * Why a runtime narrower rather than a `z.discriminatedUnion`: the wire
 * has no single discriminator field today (the union is "presence of X"-
 * shaped). Introducing a wire-level `kind` would be breaking. This is the
 * type-driven-design "parse, then narrow" pattern.
 */
export type CaptureOrderResponseNarrowed =
  | {
      kind: "fresh";
      capture: unknown;
      debugId?: string;
      summary: CaptureSummary;
      orderResult?: CaptureOrderResult;
    }
  | {
      kind: "duplicate";
      capture: unknown;
      summary: CaptureSummary;
      orderResult?: CaptureOrderResult;
      code: "DUPLICATE_ORDER";
    }
  | {
      kind: "pending";
      capture: unknown;
      debugId?: string;
      summary: CaptureSummary;
      orderResult: CaptureOrderResult;
      code: "ORDER_CREATION_FAILED";
      paymentStatus: "captured_order_pending";
    };

export function narrowCaptureResponse(
  res: CaptureOrderResponse,
): CaptureOrderResponseNarrowed {
  if (res.existingOrder === true) {
    return {
      kind: "duplicate",
      capture: res.capture,
      summary: res.summary,
      orderResult: res.orderResult,
      code: "DUPLICATE_ORDER",
    };
  }
  if (res.paymentStatus === "captured_order_pending") {
    return {
      kind: "pending",
      capture: res.capture,
      debugId: res.debugId,
      summary: res.summary,
      orderResult: res.orderResult ?? { success: false },
      code: "ORDER_CREATION_FAILED",
      paymentStatus: "captured_order_pending",
    };
  }
  return {
    kind: "fresh",
    capture: res.capture,
    debugId: res.debugId,
    summary: res.summary,
    orderResult: res.orderResult,
  };
}

